import { useMemo } from 'react'

import { Network } from '@ambire-common/interfaces/network'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useAppMode from '@web/hooks/useAppMode'
import { ModeEnum } from '@web/contexts/appModeContext'
import { EXTENSION_PRIVATE_CHAIN_IDS } from '@web/constants/extensionChains'

function filterNetworksByChainIds(networks: Network[], chainIds: number[]): Network[] {
  const set = new Set(chainIds)
  return networks.filter((n) => set.has(Number(n.chainId)))
}

/**
 * Returns networks controller state. In the extension, filters networks to
 * only those supported for the current mode (public: all chains; private: Ethereum, Arbitrum, BNB).
 */
export default function useExtensionAwareNetworks(options?: { forSettings?: boolean }) {
  const state = useNetworksControllerState()
  const { mode } = useAppMode()

  return useMemo(() => {
    // Filter on mode, not isExtension: private mode only supports RAILGUN chains and
    // must filter even on the http dev preview, where browser.runtime.id is absent.
    const isPrivateMode = mode === ModeEnum.PRIVATE
    if (!isPrivateMode) {
      return state
    }

    const networks = filterNetworksByChainIds(state.networks ?? [], EXTENSION_PRIVATE_CHAIN_IDS)
    const allNetworks = filterNetworksByChainIds(
      state.allNetworks ?? [],
      EXTENSION_PRIVATE_CHAIN_IDS
    )
    const disabledNetworks = filterNetworksByChainIds(
      state.disabledNetworks ?? [],
      EXTENSION_PRIVATE_CHAIN_IDS
    )

    return {
      ...state,
      networks,
      allNetworks,
      disabledNetworks
    }
  }, [state, mode])
}
